import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PasswordStrength, isStrongPassword } from "@/components/PasswordStrength";
import { DevicesPanel } from "@/components/DevicesPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function AccountSecurityDialog({ open, onOpenChange, forced = false, onSuccess }) {
  const [f, setF] = useState({ old_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const reset = () => setF({ old_password: "", new_password: "", confirm: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(f.new_password)) { toast.error("Le mot de passe doit contenir au moins 8 caractères, dont 1 majuscule, 1 chiffre et 1 caractère spécial"); return; }
    if (f.new_password !== f.confirm) { toast.error("La confirmation ne correspond pas"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: f.old_password, new_password: f.new_password });
      toast.success("Mot de passe modifié");
      reset();
      onSuccess?.();
      onOpenChange(false);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (forced) return; if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="security-dialog"
        className={forced ? "[&>button]:hidden" : ""}
        onInteractOutside={forced ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={forced ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><KeyRound className="h-5 w-5 text-brand-600" />{forced ? "Sécurité — Modifier mon mot de passe" : "Sécurité du compte"}</DialogTitle>
        </DialogHeader>
        {forced && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="pw-expired-notice">
            Votre mot de passe a expiré (politique de sécurité : renouvellement tous les 30 jours). Veuillez le changer pour continuer.
          </p>
        )}
        <Tabs defaultValue="pw">
        {!forced && (
          <TabsList className="grid grid-cols-2 w-full mb-2">
            <TabsTrigger value="pw" data-testid="tab-password">Mot de passe</TabsTrigger>
            <TabsTrigger value="devices" data-testid="tab-devices">Mes appareils</TabsTrigger>
          </TabsList>
        )}
        <TabsContent value="pw" className="mt-0">
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Ancien mot de passe</Label>
            <Input type="password" value={f.old_password} onChange={(e) => setF({ ...f, old_password: e.target.value })}
              required data-testid="old-password" className="mt-1" autoComplete="current-password" />
          </div>
          <div>
            <Label className="text-xs">Nouveau mot de passe</Label>
            <Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })}
              required data-testid="new-password" className="mt-1" autoComplete="new-password" />
            <PasswordStrength password={f.new_password} />
          </div>
          <div>
            <Label className="text-xs">Confirmer le nouveau mot de passe</Label>
            <Input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })}
              required data-testid="confirm-password" className="mt-1" autoComplete="new-password" />
          </div>
          <DialogFooter>
            {!forced && <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>}
            <Button type="submit" disabled={saving || !isStrongPassword(f.new_password)} data-testid="save-password" className="bg-brand-600 hover:bg-brand-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
        </TabsContent>
        {!forced && <TabsContent value="devices" className="mt-0 space-y-2">
          <p className="text-xs text-slate-500">Appareils autorisés à se connecter à votre compte. Retirer un appareil ferme ses sessions ; un code par e-mail sera redemandé.</p>
          <DevicesPanel />
        </TabsContent>}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
